import { Funnel } from "@phosphor-icons/react"
import {
  Button,
  FilterDrawer,
  FormGroup,
  FormSelector,
  QuerySelector,
  Selector,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import { Dispatch, SetStateAction, useState } from "react"
import {
  useAllCommoditiesQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useAuthenticatedUserQuery,
} from "~/generated/graphql"

export type OfferFilter = {
  com_commodity?: any
  com_variety?: any
  com_grade?: any
  oth_status?: any
  oth_startDate?: any
  oth_endDate?: any
  oth_department?: any
}

type Props = {
  filter: OfferFilter
  setFilter: Dispatch<SetStateAction<OfferFilter>>
}

export const OfferFilters = ({ filter, setFilter }: Props) => {
  const [isOpen, setOpen] = useState(false)
  const [{ data }] = useAuthenticatedUserQuery()
  const departments = data?.me?.agent?.departments?.map(
    item => item?.department
  )
  function getAppliedFilters(prefix: string, filter: any) {
    const totalApplied = Object.keys(filter)?.filter?.(
      key => key.startsWith(prefix) && filter[key]
    )?.length
    return totalApplied ? `${totalApplied} Applied` : null
  }
  return (
    <>
      <Button
        variant={Object.values(filter)?.length > 0 ? "solid" : "outline"}
        className="whitespace-nowrap"
        onClick={() => setOpen(true)}
      >
        <Funnel />
      </Button>

      <FilterDrawer
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onApply={() => setOpen(false)}
        onClearAll={() => {
          setFilter({})
          setOpen(false)
        }}
        tabs={[
          {
            id: 1,
            title: "Commodity",
            subtitle: getAppliedFilters("com", filter),
            content: (
              <div className="flex flex-col gap-3">
                <FormGroup label="Commodity" className="w-full">
                  <QuerySelector
                    serverSideQuery
                    dataHook={useAllCommoditiesQuery}
                    value={filter.com_commodity}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_commodity: value,
                      }))
                    }
                  />
                </FormGroup>
                <FormGroup label="Variety" className="w-full">
                  <QuerySelector
                    dataHook={useAllVarietiesQuery}
                    variables={{
                      commodity_id: filter.com_commodity?.id,
                    }}
                    value={filter.com_variety}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_variety: value,
                      }))
                    }
                  />
                </FormGroup>
                <FormGroup label="Grade" className="w-full">
                  <QuerySelector
                    dataHook={useAllVarietyGradesQuery}
                    variables={{
                      variety: filter.com_variety?.id,
                    }}
                    value={filter.com_grade}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        com_grade: value,
                      }))
                    }
                  />
                </FormGroup>
              </div>
            ),
          },
          {
            id: 2,
            title: "Other",
            subtitle: getAppliedFilters("oth", filter),
            content: (
              <div className="flex flex-col gap-3">
                <FormGroup label="Status" className="w-full">
                  <Selector
                    value={filter.oth_status}
                    onChange={value =>
                      setFilter(prev => ({
                        ...prev,
                        oth_status: value,
                      }))
                    }
                    options={[
                      { id: "live", name: "Live" },
                      { id: "closed", name: "Closed" },
                      { id: "created", name: "Created" },
                    ]}
                  />
                </FormGroup>
                <FormSelector
                  label="Department"
                  options={departments}
                  value={filter.oth_department}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_department: value,
                    }))
                  }
                  isClearable={false}
                />
                <FormDateInput
                  label="Date From"
                  value={filter.oth_startDate}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_startDate: value,
                    }))
                  }
                />
                <FormDateInput
                  label="Date To"
                  value={filter.oth_endDate}
                  onChange={value =>
                    setFilter(prev => ({
                      ...prev,
                      oth_endDate: value,
                    }))
                  }
                />
              </div>
            ),
          },
        ]}
      />
    </>
  )
}
